import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useDataCache } from '../context/DataCacheContext';
import LabelPrintModal from '../components/LabelPrintModal';
import ProductionTiffExportModal from '../components/ProductionTiffExportModal';
import { getProductionDesignCode, loadProductionDesignFiles } from '../utils/productionDesignFile';

// Import the original CSS for the page
import './ProductionBatchDetailsPage.css';
// Import the NEW print-specific CSS
import './BatchPrint.css';

const escapeCsvCell = (cell) => {
    if (cell === null || cell === undefined) {
        return '';
    }
    const str = String(cell);
    // If the cell contains a comma, a double quote, or a newline, wrap it in double quotes.
    if (str.includes(',') || str.includes('"') || str.includes('\n')) {
        // Escape any double quotes inside the string by doubling them up.
        const escapedStr = str.replace(/"/g, '""');
        return `"${escapedStr}"`;
    }
    return str;
};

function ProductionBatchDetailsPage() {
    const { batchId } = useParams();
    const navigate = useNavigate();
    const { currentUser, canPerformAction } = useAuth();
    const dataCache = useDataCache();
    const [batch, setBatch] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState('');
    const [showOnlyPending, setShowOnlyPending] = useState(false); // New state for filter
    const [isCreatingPo, setIsCreatingPo] = useState(false);
    // State for the receive modal
    const [isReceiveModalOpen, setIsReceiveModalOpen] = useState(false);
    const [itemToReceive, setItemToReceive] = useState(null);
    const [actualReceived, setActualReceived] = useState('');
    const [allocations, setAllocations] = useState({}); // { [lineItemId]: boolean (ticked or not) }
    const [modalError, setModalError] = useState('');
    const [isReceiveSubmitting, setIsReceiveSubmitting] = useState(false);
    const [undoReceivingSku, setUndoReceivingSku] = useState(null);
    const [selectedSkus, setSelectedSkus] = useState(new Set());
    const [isLabelPrintOpen, setIsLabelPrintOpen] = useState(false);
    const [isTiffExportOpen, setIsTiffExportOpen] = useState(false);
    const [updatingSkuStatus, setUpdatingSkuStatus] = useState(null);
    const [itemToMarkUnavailable, setItemToMarkUnavailable] = useState(null);

    const formatBatchOrderNumber = useCallback((orderNumber, source) => {
        const raw = String(orderNumber || '').trim();
        if (!raw) return '';
        if (String(source || '').trim() !== 'Shopify') return raw;
        return raw.endsWith('00') ? raw : `${raw}00`;
    }, []);

    const getAllocatedQtyForItem = (item) =>
        (item?.sourceOrders || []).reduce((sum, so) => sum + (parseFloat(so?.allocatedQuantity) || 0), 0);

    const deriveBatchStatusFromItems = (items = []) => {
        if (!Array.isArray(items) || items.length === 0) return 'Created';
        const activeItems = items.filter((it) => it?.status !== 'unavailable');
        if (activeItems.length === 0) return 'Received';
        const epsilon = 0.0001;
        const hasAnyAllocated = activeItems.some((item) => getAllocatedQtyForItem(item) > epsilon);
        const allFullyAllocated = activeItems.every((item) => {
            const total = parseFloat(item?.totalQuantity) || 0;
            const allocated = getAllocatedQtyForItem(item);
            return allocated + epsilon >= total;
        });
        if (allFullyAllocated) return 'Received';
        if (hasAnyAllocated) return 'Partially Received';
        return 'Created';
    };

    const invalidateOrdersAndInventoryCaches = useCallback(() => {
        dataCache.invalidatePrefix('orders:');
        dataCache.invalidate('inventoryPieces:/api/v1/inventory/all-pieces');
        dataCache.invalidatePrefix('inventorySummary:');
        dataCache.invalidatePrefix('inventorySummarySearch:');
        dataCache.invalidatePrefix('inventoryLedger:');
    }, [dataCache]);

    const fetchBatchDetails = useCallback(async () => {
        if (!currentUser) return;
        const cacheKey = `productionBatch:/api/v9/production-batches/${batchId}`;
        const cachedEntry = dataCache.getCacheEntry(cacheKey);
        const cachedBatch = cachedEntry?.value?.batch;
        if (cachedBatch) {
            setBatch(cachedBatch);
            setIsLoading(false);
            if (!cachedEntry.stale) return;
        }

        setIsLoading(!cachedBatch);
        try {
            const { data } = await dataCache.fetchJsonCached(
                cacheKey,
                `/api/v9/production-batches/${batchId}`,
                { force: false }
            );
            setBatch(data.batch);
        } catch (err) {
            setError(err.message);
        } finally {
            setIsLoading(false);
        }
    }, [batchId, currentUser, dataCache]);

    useEffect(() => {
        fetchBatchDetails();
    }, [fetchBatchDetails]);

    const handlePrint = () => {
        const source = document.querySelector('.printable-area .batch-print-container');
        if (!source) return;

        const iframe = document.createElement('iframe');
        iframe.setAttribute('aria-hidden', 'true');
        iframe.style.cssText = 'position:fixed;right:0;bottom:0;width:0;height:0;border:0;';
        document.body.appendChild(iframe);

        // Inline the rules so print never races a stylesheet <link> load.
        const headStyles = '<style>' + Array.from(document.styleSheets)
            .map((sheet) => {
                try {
                    return Array.from(sheet.cssRules).map((rule) => rule.cssText).join('\n');
                } catch (e) {
                    return '';
                }
            })
            .join('\n') + '</style>';
        const content = source.outerHTML.replace(/loading="lazy"/g, 'loading="eager"');

        const doc = iframe.contentWindow.document;
        doc.open();
        doc.write(`<!doctype html><html><head><meta charset="utf-8"><title>${batch.batchId}</title>
            <base href="${window.location.origin}/">
            ${headStyles}
            <style>.batch-print-container{display:block !important;} body{background:#fff;margin:0;}</style>
            </head><body>${content}</body></html>`);
        doc.close();

        const cleanup = () => {
            setTimeout(() => iframe.remove(), 500);
        };
        const triggerPrint = () => {
            const win = iframe.contentWindow;
            win.addEventListener('afterprint', cleanup);
            win.focus();
            win.print();
        };

        const images = Array.from(doc.images);
        const pending = images.filter((img) => !img.complete);
        if (pending.length === 0) {
            setTimeout(triggerPrint, 300);
            return;
        }
        let done = false;
        const go = () => {
            if (done) return;
            done = true;
            triggerPrint();
        };
        let remaining = pending.length;
        pending.forEach((img) => {
            const settle = () => { remaining -= 1; if (remaining <= 0) go(); };
            img.addEventListener('load', settle);
            img.addEventListener('error', settle);
        });
        setTimeout(go, 8000);
    };

    const getRenameName = (item, itemIndex, designOverrides) => {
        const designCode = getProductionDesignCode(item.sku, designOverrides);
        if (!designCode) {
            return '';
        }

        // CSV data starts from row 13, so index 0 is row 13, index 1 is row 14, etc.
        // The formula uses ROW()-12, so for the first data row (index 0), it should be 1.
        const rowNumberForFormula = itemIndex + 1;
        const formattedRowNumber = String(rowNumberForFormula).padStart(2, '0'); // "01", "02"

        const fabricName = item.fabricName;
        const printQuantity = parseFloat(item.totalQuantity.toFixed(2)); // Ensure it's a number for calculations

        let quantityModifier;
        if (printQuantity <= 5) {
            quantityModifier = 0.2;
        } else if (printQuantity <= 15) {
            quantityModifier = 0.4;
        } else if (printQuantity < 100) {
            quantityModifier = 0.6;
        } else if (printQuantity < 1000) {
            quantityModifier = 2;
        } else {
            quantityModifier = 0; // Default if none of the conditions met
        }
        const modifiedQuantity = (printQuantity + quantityModifier).toFixed(2); // Keep 2 decimal places
        return `${formattedRowNumber} -- ${fabricName} -- ${designCode} --- ${modifiedQuantity} Meter.tif`;
    };

    const batchLabelRows = useMemo(() => {
        if (!batch) return [];
        const rows = [];
        (batch.consolidatedItems || []).forEach((item) => {
            if (item?.status === 'unavailable') return;
            const sku = String(item?.sku || '').trim().toUpperCase();
            if (!sku) return;
            (item.sourceOrders || []).forEach((order, index) => {
                const qtyNum = Number(order?.quantity || order?.allocatedQuantity || 0);
                if (!Number.isFinite(qtyNum) || qtyNum <= 0) return;
                const orderDisplayId = formatBatchOrderNumber(order.orderNumber || order.orderId, order.source);
                rows.push({
                    batchId: batch.batchId || batchId,
                    batchNumber: batch.batchId || batchId,
                    sku,
                    qty: qtyNum,
                    orderNumber: orderDisplayId,
                    orderDisplayId,
                    lineItemId: order.lineItemId || `${sku}_${index}`,
                    fabricName: item.fabricName || '',
                    productTitle: item.fabricName || '',
                    imageUrl: item.imageUrl || item.productImageUrl || '',
                    productImageUrl: item.imageUrl || item.productImageUrl || '',
                });
            });
        });
        return rows;
    }, [batch, batchId, formatBatchOrderNumber]);

    const handleExportCsv = async () => {
        if (!batch) return;
        let designOverrides;
        try {
            designOverrides = await loadProductionDesignFiles(batch.consolidatedItems.map(item => item.sku), currentUser);
        } catch (err) {
            setError(err.message);
            return;
        }

        const getSourceOrdersString = (sourceOrders) => {
            return (sourceOrders || [])
                .map(order => {
                    const displayOrderNumber = formatBatchOrderNumber(order.orderNumber, order.source);
                    return `#${displayOrderNumber} (${order.quantity.toFixed(2)}m)`;
                })
                .join(', ');
        };

        const headers = [
            'Image', 'Batch No', 'Fabric Name', 'SKU', 'Quantity',
            'Used Quantity', 'Print quantity', 'Source Orders', 'Received Status', 'Design Code',
            'Rename name'
        ];

        const rows = [];

        // Add basic details in the first 11 rows
        rows.push(['Batch ID:', batch.batchId]);
        rows.push(['Quality:', batch.batchQuality]);
        rows.push(['Created At:', formatDate(batch.createdAt)]);
        rows.push(['Total Items:', batch.consolidatedItems.length]);
        while (rows.length < 11) {
            rows.push([]);
        }

        // Add headers at row 12
        rows.push(headers);

        // Add data rows from row 13
        batch.consolidatedItems.forEach((item, index) => {
            const designCodeRaw = getProductionDesignCode(item.sku, designOverrides);
            const designCodeWithSuffix = designCodeRaw
                ? (/\.(tif)$/i.test(designCodeRaw) ? designCodeRaw : `${designCodeRaw}.tif`)
                : '';
            const receivedStatusText = item.status === 'unavailable' ? 'Unavailable' : (item.receivedQuantity > 0 ? 'Yes' : 'No');
            const row = [
                '', batch.batchId, item.fabricName, item.sku, item.totalQuantity.toFixed(2), // Image, Batch No, Fabric Name, SKU, Quantity
                0, item.totalQuantity.toFixed(2), getSourceOrdersString(item.sourceOrders), // Used Quantity, Print quantity, Source Orders
                receivedStatusText, designCodeWithSuffix, getRenameName(item, index, designOverrides) // Received Status, Design Code, Rename name
            ];
            rows.push(row);
        });

        const csvContent = rows.map(row => row.map(escapeCsvCell).join(',')).join('\n');
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', `${batch.batchId}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const handleToggleSkuStatus = async (sku, currentStatus) => {
        if (!currentUser || !canPerformAction('batches.receive')) return;
        const nextStatus = currentStatus === 'unavailable' ? 'active' : 'unavailable';
        
        if (nextStatus === 'active') {
            if (!window.confirm(`Are you sure you want to restore SKU ${sku} to Active?`)) return;
        }

        setUpdatingSkuStatus(sku);
        setError('');
        try {
            const token = await currentUser.getIdToken();
            const response = await fetch(`/api/v9/production-batches/${batchId}/items/${encodeURIComponent(sku)}/status`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                },
                body: JSON.stringify({ status: nextStatus }),
            });
            const data = await response.json();
            if (!response.ok) {
                throw new Error(data?.error?.message || data?.message || 'Failed to update SKU status.');
            }

            setBatch(prev => {
                if (!prev) return prev;
                const nextItems = (prev.consolidatedItems || []).map(item => {
                    if (String(item?.sku || '').trim().toUpperCase() === String(sku).trim().toUpperCase()) {
                        return { ...item, status: nextStatus };
                    }
                    return item;
                });
                return { ...prev, consolidatedItems: nextItems };
            });
        } catch (err) {
            console.error(err);
            setError(err.message || 'Failed to update SKU status.');
        } finally {
            setUpdatingSkuStatus(null);
        }
    };

    const handleCreateBatchPo = async () => {
        if (!batch || !currentUser) return;
        if (!canPerformAction('batches.create')) return;

        const existingId = batch.zohoPurchaseOrderId || batch.zohoPurchaseOrder?.zohoPurchaseOrderId;
        if (existingId) {
            alert('Purchase order already created for this batch.');
            return;
        }

        if (!window.confirm(`Create Zoho Purchase Order for batch ${batch.batchId || batchId}?`)) return;

	        setIsCreatingPo(true);
	        try {
	            const token = await currentUser.getIdToken();
	            const response = await fetch(`/api/v9/production-batches/${batchId}/create-po`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}` },
            });
	            const data = await response.json().catch(() => ({}));
	            if (!response.ok) throw new Error(data.message || 'Failed to create purchase order.');
		            alert(data.message || 'Purchase order created.');
		            dataCache.invalidate(`productionBatch:/api/v9/production-batches/${batchId}`);
		            dataCache.invalidate('productionBatches:/api/v9/production-batches');
		            invalidateOrdersAndInventoryCaches();
		            await fetchBatchDetails();
		        } catch (err) {
		            alert(`Error: ${err.message}`);
		        } finally {
	            setIsCreatingPo(false);
	        }
	    };

    useEffect(() => {
        setSelectedSkus(new Set());
    }, [batchId, showOnlyPending]);

    const getSelectableItems = () => {
        if (!batch) return [];
        const consolidatedItems = batch.consolidatedItems || [];
        return consolidatedItems.filter(item => {
            const receivedTotal = parseFloat(item.receivedQuantity) || 0;
            const totalQty = parseFloat(item.totalQuantity) || 0;
            const pendingQty = Math.max(0, totalQty - receivedTotal);
            if (pendingQty <= 0) return false;

            if (!showOnlyPending) return true;
            const allocatedToOrders = item.sourceOrders?.reduce((sum, so) => sum + (so.allocatedQuantity || 0), 0) || 0;
            return allocatedToOrders < totalQty;
        });
    };

    const isAllSelected = () => {
        const selectable = getSelectableItems();
        if (selectable.length === 0) return false;
        return selectable.every(item => selectedSkus.has(item.sku));
    };

    const handleSelectAllToggle = () => {
        const selectable = getSelectableItems();
        if (isAllSelected()) {
            setSelectedSkus(prev => {
                const next = new Set(prev);
                selectable.forEach(item => next.delete(item.sku));
                return next;
            });
        } else {
            setSelectedSkus(prev => {
                const next = new Set(prev);
                selectable.forEach(item => next.add(item.sku));
                return next;
            });
        }
    };

    const handleSelectRowToggle = (sku) => {
        setSelectedSkus(prev => {
            const next = new Set(prev);
            if (next.has(sku)) {
                next.delete(sku);
            } else {
                next.add(sku);
            }
            return next;
        });
    };

    const handleBulkReceiveFull = async () => {
        if (selectedSkus.size === 0) return;
        
        if (!window.confirm(`Are you sure you want to fully receive the remaining quantities for all ${selectedSkus.size} selected item(s)?`)) {
            return;
        }

        setError('');
        setIsReceiveSubmitting(true);

        const selectableItems = batch?.consolidatedItems || [];
        const selectedItemsData = selectableItems.filter(item => selectedSkus.has(item.sku));

        const receivedItems = selectedItemsData.map(item => {
            const sourceOrders = Array.isArray(item.sourceOrders) ? item.sourceOrders : [];

            // Allocate full remaining quantities to pending orders
            const finalAllocations = sourceOrders.map(so => ({
                lineItemId: so.lineItemId,
                allocatedQuantity: !(parseFloat(so.allocatedQuantity) > 0) ? so.quantity : 0
            }));

            return {
                sku: item.sku,
                allocations: finalAllocations,
                toStockQuantity: 0
            };
        });

        try {
            const token = await currentUser.getIdToken();
            const response = await fetch(`/api/v9/production-batches/${batchId}/receive`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                body: JSON.stringify({ receivedItems })
            });
            const result = await response.json();
            if (!response.ok) throw new Error(result?.error?.message || result.message || 'Failed to submit received items.');

            // Update all received items locally in the state
            setBatch((prev) => {
                if (!prev) return prev;
                const consolidatedItems = Array.isArray(prev.consolidatedItems) ? prev.consolidatedItems : [];
                const nextItems = consolidatedItems.map((ci) => {
                    const matchedSelected = receivedItems.find(r => r.sku === ci.sku);
                    if (!matchedSelected) return ci;

                    // Calculate total quantity we received (sum of allocations)
                    const totalAllocated = matchedSelected.allocations.reduce((sum, a) => sum + a.allocatedQuantity, 0);

                    const nextSourceOrders = (ci.sourceOrders || []).map((so) => {
                        const prevAllocated = parseFloat(so.allocatedQuantity) || 0;
                        const matchedAllocation = matchedSelected.allocations.find(a => a.lineItemId === so.lineItemId);
                        const deltaAllocated = matchedAllocation ? matchedAllocation.allocatedQuantity : 0;
                        return {
                            ...so,
                            allocatedQuantity: prevAllocated + deltaAllocated,
                        };
                    });

                    const prevReceived = parseFloat(ci.receivedQuantity) || 0;
                    return {
                        ...ci,
                        receivedQuantity: prevReceived + totalAllocated,
                        sourceOrders: nextSourceOrders,
                    };
                });

                const newStatus = deriveBatchStatusFromItems(nextItems);
                return { ...prev, consolidatedItems: nextItems, status: newStatus };
            });

            setSelectedSkus(new Set());
            dataCache.invalidate(`productionBatch:/api/v9/production-batches/${batchId}`);
            dataCache.invalidate('productionBatches:/api/v9/production-batches');
            invalidateOrdersAndInventoryCaches();
        } catch (err) {
            setError(err.message);
        } finally {
            setIsReceiveSubmitting(false);
        }
    };

    const handleOpenReceiveModal = (item) => {
        if (!item) return;
        setItemToReceive(item);
        const sourceOrders = Array.isArray(item.sourceOrders) ? item.sourceOrders : [];
        const alreadyAllocated = sourceOrders.reduce((sum, so) => sum + (parseFloat(so.allocatedQuantity) || 0), 0);
        const pendingQty = Math.max(0, (parseFloat(item.totalQuantity) || 0) - alreadyAllocated);
        // For partial receives, default to pending allocatable qty.
        setActualReceived(pendingQty.toFixed(2));
        // Pre-tick only unallocated orders (pending lines).
        const initialAllocations = sourceOrders.reduce((allocAcc, so) => {
            allocAcc[so.lineItemId] = !(parseFloat(so.allocatedQuantity) > 0);
            return allocAcc;
        }, {});
        setAllocations(initialAllocations);
        setModalError(''); // Clear any previous modal errors
        setIsReceiveModalOpen(true);
    };

    const handleCloseReceiveModal = () => {
        setIsReceiveModalOpen(false);
        setItemToReceive(null);
        setModalError('');
    };

    const handleActualReceivedChange = (value) => {
        setActualReceived(value);
        if (!itemToReceive) return;
        const received = parseFloat(value) || 0;
        const sourceOrders = Array.isArray(itemToReceive.sourceOrders) ? itemToReceive.sourceOrders : [];
        const alreadyAllocated = sourceOrders.reduce((sum, so) => sum + (parseFloat(so.allocatedQuantity) || 0), 0);
        const expected = Math.max(0, (parseFloat(itemToReceive.totalQuantity) || 0) - alreadyAllocated);

        // If received quantity is enough, auto-tick all orders.
        if (received >= expected) {
            const allTicked = sourceOrders.reduce((acc, so) => {
                acc[so.lineItemId] = !(parseFloat(so.allocatedQuantity) > 0);
                return acc;
            }, {});
            setAllocations(allTicked);
        } else {
            // If received quantity is less than expected, un-tick all orders.
            const allUnTicked = sourceOrders.reduce((acc, so) => {
                acc[so.lineItemId] = false;
                return acc;
            }, {});
            setAllocations(allUnTicked);
        }
    };

    const handleToggleAllocation = (lineItemId) => {
        if (!itemToReceive) return;
        const currentlyAllocated = itemToReceive.sourceOrders.reduce((sum, order) => {
            return allocations[order.lineItemId] ? sum + order.quantity : sum;
        }, 0);

        const clickedOrder = itemToReceive.sourceOrders.find(o => o.lineItemId === lineItemId);
        const received = parseFloat(actualReceived) || 0;

        // An order that already got its fabric in an earlier receive must not be allocated again
        if (!allocations[lineItemId] && (parseFloat(clickedOrder?.allocatedQuantity) || 0) > 0) {
            alert(`Order #${clickedOrder.orderNumber} already received its fabric from this batch.`);
            return;
        }

        // If trying to tick an order, check if it would exceed the received amount
        if (!allocations[lineItemId]) {
            if (currentlyAllocated + clickedOrder.quantity > received) {
                alert(`Cannot allocate. Total allocated would exceed received quantity of ${received}m.`);
                return;
            }
        }
        setAllocations(prev => ({ ...prev, [lineItemId]: !prev[lineItemId] }));
    };

    const handleReceiveSubmit = async (e) => {
        e.preventDefault();
        if (!itemToReceive) return;

        setModalError('');
        setIsReceiveSubmitting(true);

        const totalAllocatedToOrders = itemToReceive.sourceOrders.reduce((sum, order) => {
            return allocations[order.lineItemId] ? sum + order.quantity : sum;
        }, 0);

        const toStockQuantity = (parseFloat(actualReceived) || 0) - totalAllocatedToOrders;

        const finalAllocations = itemToReceive.sourceOrders.map(so => ({
            lineItemId: so.lineItemId,
            allocatedQuantity: allocations[so.lineItemId] ? so.quantity : 0
        }));

        const receivedData = { sku: itemToReceive.sku, allocations: finalAllocations, toStockQuantity };

        try {
            const token = await currentUser.getIdToken();
            const response = await fetch(`/api/v9/production-batches/${batchId}/receive`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                body: JSON.stringify({ receivedItems: [receivedData] })
            });
            const result = await response.json();
            if (!response.ok) throw new Error(result?.error?.message || result.message || 'Failed to submit received items.');
            // Close modal and update only the affected SKU in local state (no full reload, no success popup).
            const received = parseFloat(actualReceived) || 0;
            setBatch((prev) => {
                if (!prev) return prev;
                const consolidatedItems = Array.isArray(prev.consolidatedItems) ? prev.consolidatedItems : [];
                const nextItems = consolidatedItems.map((ci) => {
                    if (ci.sku !== itemToReceive.sku) return ci;
                    const nextSourceOrders = (ci.sourceOrders || []).map((so) => {
                        const prevAllocated = parseFloat(so.allocatedQuantity) || 0;
                        const deltaAllocated = allocations[so.lineItemId] ? so.quantity : 0;
                        return {
                            ...so,
                            allocatedQuantity: prevAllocated + deltaAllocated,
                        };
                    });
                    const prevReceived = parseFloat(ci.receivedQuantity) || 0;
                    return {
                        ...ci,
                        receivedQuantity: prevReceived + received,
                        sourceOrders: nextSourceOrders,
                    };
                });

                const newStatus = deriveBatchStatusFromItems(nextItems);
                return { ...prev, consolidatedItems: nextItems, status: newStatus };
            });

	            handleCloseReceiveModal();
	            dataCache.invalidate(`productionBatch:/api/v9/production-batches/${batchId}`);
	            dataCache.invalidate('productionBatches:/api/v9/production-batches');
	            invalidateOrdersAndInventoryCaches();
	        } catch (err) {
	            setModalError(err.message);
	        } finally {
            setIsReceiveSubmitting(false);
        }
    };

    const handleUndoReceive = async (sku) => {
        if (!window.confirm(`Are you sure you want to undo receiving for SKU ${sku}? This will revert stock and order fulfillment status.`)) return;
        setError('');
        setUndoReceivingSku(sku);
        try {
            const token = await currentUser.getIdToken();
            const response = await fetch(`/api/v9/production-batches/${batchId}/undo-receive`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                body: JSON.stringify({ sku }),
            });
            const result = await response.json();
            if (!response.ok) throw new Error(result.message || 'Failed to undo receive.');
            // Update only the affected SKU in local state (no full reload, no success popup).
            setBatch((prev) => {
                if (!prev) return prev;
                const consolidatedItems = Array.isArray(prev.consolidatedItems) ? prev.consolidatedItems : [];
                const nextItems = consolidatedItems.map((ci) => {
                    if (ci.sku !== sku) return ci;
                    const nextSourceOrders = (ci.sourceOrders || []).map((so) => {
                        const { allocatedQuantity, ...rest } = so;
                        return rest;
                    });
                    const { receivedQuantity, ...restCi } = ci;
                    return { ...restCi, sourceOrders: nextSourceOrders };
                });

                const newStatus = deriveBatchStatusFromItems(nextItems);
                return { ...prev, consolidatedItems: nextItems, status: newStatus };
            });
	            dataCache.invalidate(`productionBatch:/api/v9/production-batches/${batchId}`);
	            dataCache.invalidate('productionBatches:/api/v9/production-batches');
	            invalidateOrdersAndInventoryCaches();
	        } catch (err) {
	            setError(err.message);
	        } finally {
            setUndoReceivingSku(null);
        }
    };

    const formatDate = (isoString) => {
        if (!isoString) return 'N/A';
        return new Date(isoString).toLocaleDateString('en-GB', {
            day: '2-digit', month: 'short', year: 'numeric',
        });
    };

    if (isLoading) {
        return <div className="loading-overlay"><p>Loading Batch Details...</p></div>;
    }

    if (error) {
        return <div className="error-message">{error}</div>;
    }

    if (!batch) {
        return <div className="production-batch-details-container"><p>Batch not found.</p></div>;
    }

    const displayBatchStatus = deriveBatchStatusFromItems(batch?.consolidatedItems || []);

    // Calculate fabric summary for the printout
    const fabricSummary = (batch.consolidatedItems || []).reduce((summary, item) => {
        const fabric = item.fabricName || 'Unspecified Fabric';
        if (!summary[fabric]) {
            summary[fabric] = { totalQuantity: 0 };
        }
        summary[fabric].totalQuantity += item.totalQuantity;
        return summary;
    }, {});

    const fabricSummaryArray = Object.entries(fabricSummary).map(([fabricName, data]) => ({
        fabricName,
        totalQuantity: data.totalQuantity
    })).sort((a, b) => a.fabricName.localeCompare(b.fabricName)); // Sort for consistency

    const totalFabricQuantity = fabricSummaryArray.reduce((total, item) => total + item.totalQuantity, 0);
    const isMixedBulkLot =
        String(batch?.sourceType || '').toLowerCase() === 'po_bulk' ||
        String(batch?.batchQuality || '').toLowerCase() === 'mixed';
    const printItemsByQuality = (batch.consolidatedItems || []).reduce((acc, item) => {
        const quality = String(item?.batchQuality || 'Unknown').trim() || 'Unknown';
        if (!acc[quality]) acc[quality] = [];
        acc[quality].push(item);
        return acc;
    }, {});
    const orderedQualityKeys = Object.keys(printItemsByQuality).sort((a, b) => a.localeCompare(b));

    // The main return with both on-screen and printable content
    return (
        <>
            {/* This is the content that shows on the screen */}
            <div className="production-batch-details-container">
                <div className="page-header">
                    <h1>Batch Details: {batch.batchId}</h1>
                    <div className="header-actions">
                        <button onClick={() => navigate('/batches')} className="action-button back-btn">
                            Back to Production
                        </button>
                        {canPerformAction('batches.create') && (
                            <button
                                onClick={handleCreateBatchPo}
                                className="action-button export-btn"
                                disabled={
                                    isCreatingPo ||
                                    !!(batch?.zohoPurchaseOrderId || batch?.zohoPurchaseOrder?.zohoPurchaseOrderId)
                                }
                                title="Create Zoho Purchase Order for this batch"
                            >
                                {batch?.zohoPurchaseOrderId || batch?.zohoPurchaseOrder?.zohoPurchaseOrderId
                                    ? `PO: ${batch?.poDisplayNumber || batch?.zohoPurchaseOrder?.poDisplayNumber || batch?.batchId || 'Created'}`
                                    : (isCreatingPo ? 'Creating PO...' : 'Create PO')}
                            </button>
                        )}
                        {canPerformAction('batches.exportCsv') && (
                            <button onClick={handleExportCsv} className="action-button export-btn">
                                Export CSV
                            </button>
                        )}
                        {canPerformAction('batches.prepareProductionFiles') && (
                            <button
                                onClick={() => setIsTiffExportOpen(true)}
                                className="action-button export-btn"
                                style={{ backgroundColor: '#0284c7', borderColor: '#0284c7' }}
                                title="Prepare and copy TIFF files for production"
                            >
                                Prepare Production TIFFs
                            </button>
                        )}
                        {canPerformAction('inventory.printLabels') && (
                            <button
                                onClick={() => setIsLabelPrintOpen(true)}
                                className="action-button print-btn"
                                disabled={batchLabelRows.length === 0}
                                title={batchLabelRows.length === 0 ? 'No source-order labels found for this batch.' : 'Print batch labels on ST-33 sheets'}
                            >
                                Print Labels
                            </button>
                        )}
                        {canPerformAction('batches.printPdf') && (
                            <button onClick={handlePrint} className="action-button print-btn">
                                Print to PDF
                            </button>
                        )}
                    </div>
                </div>

                <div className="batch-info-card">
                    <h2>Batch Information</h2>
                    <p><strong>Status:</strong> {displayBatchStatus}</p>
                    <p><strong>Quality:</strong> {batch.batchQuality}</p>
                    <p><strong>Created:</strong> {formatDate(batch.createdAt)}</p>
                    <p>
                        <strong>Production Run (TIFF):</strong>{' '}
                        {batch.tiffRunAt ? (
                            (() => {
                                const createdDate = new Date(batch.createdAt);
                                const runDate = new Date(batch.tiffRunAt);
                                const diffDays = Math.max(0, Math.round((runDate - createdDate) / (1000 * 60 * 60 * 24)));
                                const daysText = diffDays === 0 ? 'Same day' : diffDays === 1 ? '1 day after creation' : `${diffDays} days after creation`;
                                return (
                                    <span style={{ color: '#059669', fontWeight: '600' }}>
                                        {formatDate(batch.tiffRunAt)} ({daysText})
                                    </span>
                                );
                            })()
                        ) : (
                            (() => {
                                const createdDate = new Date(batch.createdAt);
                                const ageDays = Math.max(0, Math.floor((new Date() - createdDate) / (1000 * 60 * 60 * 24)));
                                const pendingText = ageDays === 0 ? 'Created today' : `${ageDays}d pending`;
                                return (
                                    <span style={{ color: '#d97706', fontWeight: '500' }}>
                                        Not run yet ({pendingText})
                                    </span>
                                );
                            })()
                        )}
                    </p>
                </div>

                {fabricSummaryArray.length > 0 && (
                    <div className="batch-info-card">
                        <h2>Fabric Summary</h2>
                        <table className="fabric-summary-table">
                            <thead>
                                <tr>
                                    <th>Fabric Name</th>
                                    <th className="num-col">Total Quantity (m)</th>
                                </tr>
                            </thead>
                            <tbody>
                                {fabricSummaryArray.map((summaryItem) => (
                                    <tr key={summaryItem.fabricName}>
                                        <td>{summaryItem.fabricName}</td>
                                        <td className="num-col">{summaryItem.totalQuantity.toFixed(2)}</td>
                                    </tr>
                                ))}
                            </tbody>
                            <tfoot>
                                <tr>
                                    <td><strong>Total</strong></td>
                                    <td className="num-col"><strong>{totalFabricQuantity.toFixed(2)}</strong></td>
                                </tr>
                            </tfoot>
                        </table>
                    </div>
                )}

                <div className="items-to-produce-section">
                    <div className="section-header">
                        <h2>Batch Items</h2>
                        <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                            {selectedSkus.size > 0 && canPerformAction('batches.receive') && (
                                <button
                                    onClick={handleBulkReceiveFull}
                                    className="action-button receive-btn-small"
                                    disabled={isReceiveSubmitting}
                                >
                                    {isReceiveSubmitting ? 'Receiving...' : `Receive Selected (Full) (${selectedSkus.size})`}
                                </button>
                            )}
                            <button
                                onClick={() => setShowOnlyPending(prev => !prev)}
                                className={`action-button filter-btn ${showOnlyPending ? 'active' : ''}`}
                            >
                                {showOnlyPending ? 'Show All Items' : 'Show Only Pending'}
                            </button>
                        </div>
                    </div>
                    <div className="table-wrapper">
                        <table className="items-table">
                            <thead>
                                <tr>
                                    {canPerformAction('batches.receive') && (
                                        <th style={{ width: '40px', textAlign: 'center' }}>
                                            <input
                                                type="checkbox"
                                                checked={isAllSelected()}
                                                onChange={handleSelectAllToggle}
                                                style={{ cursor: 'pointer', transform: 'scale(1.2)' }}
                                            />
                                        </th>
                                    )}
                                    <th>Image</th>
                                    <th>SKU</th>
                                    <th>Fabric</th>
                                    <th className="num-col">Total Qty (m)</th>
                                    {canPerformAction('batches.viewStockDetails') && (
                                        <>
                                            <th className="num-col">Received Total (m)</th>
                                            <th className="num-col">Allocated to Orders (m)</th>
                                            <th className="num-col">To Stock (m)</th>
                                        </>
                                    )}
                                    <th className="num-col">Pending Qty (m)</th>
                                    <th>Source Orders</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {batch.consolidatedItems
                                    ?.filter(item => {
                                        if (item?.status === 'unavailable') {
                                            return !showOnlyPending; // Hide from pending filter
                                        }
                                        if (!showOnlyPending) return true; // Show all if filter is off
                                        const allocatedToOrders = item.sourceOrders?.reduce((sum, so) => sum + (so.allocatedQuantity || 0), 0) || 0;
                                        const totalQty = parseFloat(item.totalQuantity) || 0;
                                        return allocatedToOrders < totalQty;
                                    })
                                    .map(item => {
                                        const isUnavailable = item.status === 'unavailable';
                                        const totalQty = parseFloat(item.totalQuantity) || 0;
                                        const receivedTotal = parseFloat(item.receivedQuantity) || 0;
                                        const allocatedToOrders = item.sourceOrders?.reduce((sum, so) => sum + (so.allocatedQuantity || 0), 0) || 0;
                                        const toStockQty = Math.max(0, receivedTotal - allocatedToOrders);
                                        const pendingQty = isUnavailable ? 0 : Math.max(0, totalQty - receivedTotal);

                                        return (
                                            <tr
                                                key={item.sku}
                                                className={`${receivedTotal > 0 ? 'item-received' : ''} ${isUnavailable ? 'item-unavailable' : ''}`}
                                                style={isUnavailable ? { opacity: 0.6, backgroundColor: '#fef2f2' } : {}}
                                            >
                                                {canPerformAction('batches.receive') && (
                                                    <td style={{ textAlign: 'center' }}>
                                                        {!isUnavailable && pendingQty > 0 ? (
                                                            <input
                                                                type="checkbox"
                                                                checked={selectedSkus.has(item.sku)}
                                                                onChange={() => handleSelectRowToggle(item.sku)}
                                                                style={{ cursor: 'pointer', transform: 'scale(1.2)' }}
                                                            />
                                                        ) : (
                                                            <input type="checkbox" disabled style={{ opacity: 0.5 }} />
                                                        )}
                                                    </td>
                                                )}
                                        <td>
                                            {item.imageUrl ? (
                                                <img src={item.imageUrl} alt={item.sku} className="item-image" />
                                            ) : (
                                                <div className="no-image">N/A</div>
                                            )}
                                        </td>
                                        <td>
                                            <span style={isUnavailable ? { textDecoration: 'line-through' } : {}}>{item.sku}</span>
                                            {isUnavailable && (
                                                <span style={{ marginLeft: '6px', fontSize: '0.75rem', backgroundColor: '#fee2e2', color: '#b91c1c', padding: '2px 6px', borderRadius: '4px', fontWeight: 'bold' }}>
                                                    UNAVAILABLE
                                                </span>
                                            )}
                                        </td>
                                        <td>{item.fabricName}</td>
                                        <td className="num-col">{item.totalQuantity.toFixed(2)}</td>
                                        {canPerformAction('batches.viewStockDetails') && (
                                            <>
                                                <td className="num-col">{receivedTotal.toFixed(2)}</td>
                                                <td className="num-col">{allocatedToOrders.toFixed(2)}</td>
                                                <td className="num-col">{toStockQty.toFixed(2)}</td>
                                            </>
                                        )}
                                        <td className="num-col">{pendingQty.toFixed(2)}</td>
                                        <td>
                                            <ul className="order-breakdown-list-inline">
                                                {item.sourceOrders?.map(order => {
                                                    const isAllocated = item.receivedQuantity !== undefined && order.allocatedQuantity > 0;
                                                    const isPending = item.receivedQuantity !== undefined && !(order.allocatedQuantity > 0);
                                                    const statusClass = isAllocated ? 'allocated' : (isPending ? 'pending' : '');
                                                    const displayOrderNumber = formatBatchOrderNumber(order.orderNumber, order.source);
                                                    return (
                                                        <li key={order.lineItemId} className={statusClass}>
                                                            #{displayOrderNumber} ({order.quantity.toFixed(2)}m)
                                                        </li>
                                                    );
                                                })}
                                            </ul>
                                        </td>
                                        <td>
                                            {isUnavailable ? (
                                                <div className="batch-item-actions-stack">
                                                    <button
                                                        onClick={() => handleToggleSkuStatus(item.sku, item.status)}
                                                        className="action-button mark-active-btn-small"
                                                        disabled={updatingSkuStatus === item.sku}
                                                    >
                                                        {updatingSkuStatus === item.sku ? 'Updating...' : 'Mark Active'}
                                                    </button>
                                                </div>
                                            ) : (
                                                <div className="batch-item-actions-stack">
                                                    {(pendingQty > 0 && canPerformAction('batches.receive')) && (
                                                        <button
                                                            onClick={() => handleOpenReceiveModal(item)}
                                                            className="action-button receive-btn-small"
                                                            disabled={isReceiveSubmitting}
                                                        >
                                                            Receive
                                                        </button>
                                                    )}
                                                    {(receivedTotal > 0 && canPerformAction('batches.undoReceive')) && (
                                                        <button
                                                            onClick={() => handleUndoReceive(item.sku)}
                                                            className="action-button undo-btn"
                                                            disabled={undoReceivingSku === item.sku}
                                                        >
                                                            {undoReceivingSku === item.sku ? 'Undoing...' : 'Undo'}
                                                        </button>
                                                    )}
                                                    {receivedTotal === 0 && canPerformAction('batches.receive') && (
                                                        <button
                                                            onClick={() => setItemToMarkUnavailable(item)}
                                                            className="action-button unavailable-btn-small"
                                                            disabled={updatingSkuStatus === item.sku}
                                                        >
                                                            {updatingSkuStatus === item.sku ? 'Updating...' : 'Unavailable'}
                                                        </button>
                                                    )}
                                                </div>
                                            )}
                                        </td>
                                            </tr>
                                        );
                                    })}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>

            {isReceiveModalOpen && itemToReceive && (
                <div className="modal-overlay">
                    <div className="modal-content receive-item-modal-content">
                        <h2>Receive & Allocate: {itemToReceive.sku}</h2>
                        <p>Enter the actual quantity received and allocate it to orders or to general stock.</p>
                        {modalError && <p className="form-error">{modalError}</p>}
                        <form onSubmit={handleReceiveSubmit}>
                            <div className="actual-received-input-group">
                                <label>Actual Received Quantity (m)</label>
                                <input
                                    type="number"
                                    step="0.25"
                                    min="0"
                                    value={actualReceived}
                                    onChange={(e) => handleActualReceivedChange(e.target.value)}
                                    className="quantity-input"
                                />
                            </div>

                            {(parseFloat(actualReceived) || 0) < itemToReceive.totalQuantity && (
                                <div className="receive-items-table-wrapper">
                                    <h4>Allocate to Source Orders</h4>
                                    <table className="receive-items-table">
                                        <thead>
                                            <tr>
                                                <th>Source Order</th>
                                                <th>Required</th>
                                                <th>Allocate?</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {itemToReceive.sourceOrders.map(so => (
                                                <tr key={so.lineItemId}>
                                                    <td>Order #{so.orderNumber}</td>
                                                    <td>{so.quantity.toFixed(2)}m</td>
                                                    <td className="allocation-actions">
                                                        {(parseFloat(so.allocatedQuantity) || 0) > 0 ? (
                                                            <span className="already-allocated">Already received</span>
                                                        ) : (
                                                            <>
                                                                <button type="button" className={`tick-btn ${allocations[so.lineItemId] ? 'active' : ''}`} onClick={() => handleToggleAllocation(so.lineItemId)}>✓</button>
                                                                <button type="button" className={`cross-btn ${!allocations[so.lineItemId] ? 'active' : ''}`} onClick={() => handleToggleAllocation(so.lineItemId)}>✗</button>
                                                            </>
                                                        )}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}

                            <div className="to-stock-summary">
                                <span>Allocated to Orders: <strong>
                                    {itemToReceive.sourceOrders.reduce((sum, order) => allocations[order.lineItemId] ? sum + order.quantity : sum, 0).toFixed(2)}m
                                </strong></span>
                                <span>To General Stock: <strong>
                                    {
                                        (() => {
                                            const received = parseFloat(actualReceived) || 0;
                                            const allocated = itemToReceive.sourceOrders.reduce((sum, order) => allocations[order.lineItemId] ? sum + order.quantity : sum, 0);
                                            const toStock = received - allocated;
                                            return toStock > 0 ? toStock.toFixed(2) : '0.00';
                                        })()
                                    }m
                                </strong></span>
                            </div>

                            <div className="modal-actions">
                                <button type="button" onClick={handleCloseReceiveModal} className="action-button" disabled={isReceiveSubmitting}>Cancel</button>
                                <button type="submit" className="action-button receive-btn-small" disabled={isReceiveSubmitting}>
                                    {isReceiveSubmitting ? 'Submitting...' : 'Confirm & Reconcile'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {itemToMarkUnavailable && (
                <div className="modal-overlay">
                    <div className="modal-content" style={{ maxWidth: '520px', borderRadius: '12px', padding: '1.75rem' }}>
                        <h2 style={{ color: '#b91c1c', marginTop: '0', marginBottom: '0.75rem', fontSize: '1.35rem' }}>
                            ⚠️ Confirm Mark Unavailable
                        </h2>
                        <p style={{ color: '#334155', fontSize: '1rem', lineHeight: '1.5', marginBottom: '1rem' }}>
                            Are you sure you want to mark SKU <strong>{itemToMarkUnavailable.sku}</strong> as <strong>Unavailable</strong>?
                        </p>
                        <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '8px', padding: '12px 14px', marginBottom: '1.5rem', fontSize: '0.9rem', color: '#991b1b' }}>
                            <ul style={{ margin: '0', paddingLeft: '20px', display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                <li>Fabric: <strong>{itemToMarkUnavailable.fabricName}</strong> ({itemToMarkUnavailable.totalQuantity}m)</li>
                                <li>Will be <strong>removed / cancelled</strong> from the linked Zoho Purchase Order.</li>
                                <li>Will be <strong>excluded</strong> from pending fabric calculations & WhatsApp reports.</li>
                            </ul>
                        </div>
                        <div className="modal-actions" style={{ marginTop: '1rem', paddingTop: '1rem', display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
                            <button
                                type="button"
                                onClick={() => setItemToMarkUnavailable(null)}
                                className="action-button"
                                disabled={updatingSkuStatus === itemToMarkUnavailable.sku}
                                style={{ backgroundColor: '#edf2f7', color: '#334155', border: '1px solid #cbd5e1', padding: '8px 16px', borderRadius: '6px', fontWeight: '600' }}
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                onClick={async () => {
                                    const sku = itemToMarkUnavailable.sku;
                                    setItemToMarkUnavailable(null);
                                    await handleToggleSkuStatus(sku, 'active');
                                }}
                                className="action-button"
                                disabled={updatingSkuStatus === itemToMarkUnavailable.sku}
                                style={{ width: 'auto', padding: '8px 18px', backgroundColor: '#dc2626', color: '#fff', border: 'none', borderRadius: '6px', fontWeight: '600' }}
                            >
                                {updatingSkuStatus === itemToMarkUnavailable.sku ? 'Marking...' : 'Yes, Mark Unavailable'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <LabelPrintModal
                isOpen={isLabelPrintOpen}
                onClose={() => setIsLabelPrintOpen(false)}
                modeContext="batch"
                batchId={batch?.batchId || batchId}
                batchLabelRows={batchLabelRows}
            />

            <ProductionTiffExportModal
                isOpen={isTiffExportOpen}
                onClose={() => setIsTiffExportOpen(false)}
                batch={batch}
                onRunSuccess={fetchBatchDetails}
            />

            {/* This is the hidden, styled section that will be printed. */}
            {/* It is outside the main container to avoid inheriting unwanted styles */}
            <div className="printable-area">
                <div className="batch-print-container">
                    <header className="print-header">
                        <h1>{batch.batchId}</h1>
                    </header>

                    <div className="batch-details-grid">
                        <div className="detail-item">
                            <span className="detail-label">Batch ID</span>
                            <span className="detail-value">{batch.batchId}</span>
                        </div>
                        <div className="detail-item">
                            <span className="detail-label">Quality</span>
                            <span className="detail-value">{batch.batchQuality}</span>
                        </div>
                        <div className="detail-item">
                            <span className="detail-label">Created</span>
                            <span className="detail-value">{formatDate(batch.createdAt)}</span>
                        </div>
                    </div>

                    {/* Fabric Summary Table for Print */}
                    {fabricSummaryArray.length > 0 && (
                        <div className="print-summary-section">
                            <h2>Fabric Summary</h2>
                            <table className="print-items-table summary-table">
                                <thead>
                                    <tr>
                                        <th>Fabric Name</th>
                                        <th>Total Quantity (m)</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {fabricSummaryArray.map(summaryItem => (
                                        <tr key={summaryItem.fabricName}>
                                            <td>{summaryItem.fabricName}</td>
                                            <td className="num-col">{summaryItem.totalQuantity.toFixed(2)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                                <tfoot>
                                    <tr>
                                        <td><strong>Total</strong></td>
                                        <td className="num-col"><strong>{totalFabricQuantity.toFixed(2)}</strong></td>
                                    </tr>
                                </tfoot>
                            </table>
                        </div>
                    )}

                    <div className="print-items-section">
                        <h2>SKU Details</h2>
                        <table className="print-items-table">
                            <thead>
                                <tr>
                                    <th>Image</th>
                                    <th>SKU</th>
                                    <th>Fabric</th>
                                    <th className="num-col">Total Qty (m)</th>
                                    <th className="num-col">Received Total (m)</th>
                                    <th className="num-col">Allocated to Orders (m)</th>
                                    <th className="num-col">To Stock (m)</th>
                                    <th className="num-col">Pending Qty (m)</th>
                                    <th>Source Orders</th>
                                </tr>
                            </thead>
                            <tbody>
                                {(isMixedBulkLot ? orderedQualityKeys.flatMap((quality) => {
                                    const rows = (printItemsByQuality[quality] || []).map((item) => {
                                        const isUnavailable = item.status === 'unavailable';
                                        const totalQty = parseFloat(item.totalQuantity) || 0;
                                        const receivedTotal = parseFloat(item.receivedQuantity) || 0;
                                        const allocatedToOrders = item.sourceOrders?.reduce((sum, so) => sum + (so.allocatedQuantity || 0), 0) || 0;
                                        const toStockQty = Math.max(0, receivedTotal - allocatedToOrders);
                                        const pendingQty = isUnavailable ? 0 : Math.max(0, totalQty - receivedTotal);
                                        return (
                                            <tr key={`${quality}_${item.sku}`} style={isUnavailable ? { opacity: 0.6 } : {}}>
                                                <td className="print-image-cell">
                                                    {item.imageUrl ? (
                                                        <img src={item.imageUrl} alt={item.sku} className="print-item-image" loading="lazy" />
                                                    ) : (
                                                        <div className="print-no-image">No Image</div>
                                                    )}
                                                </td>
                                                <td className="sku-cell">
                                                    <span style={isUnavailable ? { textDecoration: 'line-through' } : {}}>{item.sku}</span>
                                                    {isUnavailable && <span style={{ color: '#b91c1c', fontSize: '0.75rem', marginLeft: '4px' }}>(Unavailable)</span>}
                                                </td>
                                                <td>{item.fabricName}</td>
                                                <td className="num-col">{item.totalQuantity.toFixed(2)}</td>
                                                <td className="num-col">{receivedTotal.toFixed(2)}</td>
                                                <td className="num-col">{allocatedToOrders.toFixed(2)}</td>
                                                <td className="num-col">{toStockQty.toFixed(2)}</td>
                                                <td className="num-col">{pendingQty.toFixed(2)}</td>
                                                <td>
                                                    <ul className="source-orders-list">
                                                        {item.sourceOrders?.map(order => (
                                                            <li key={order.lineItemId}>
                                                                <span>#{formatBatchOrderNumber(order.orderNumber, order.source)}</span>
                                                                <span>{order.quantity.toFixed(2)}m</span>
                                                            </li>
                                                        ))}
                                                    </ul>
                                                </td>
                                            </tr>
                                        );
                                    });
                                    return [
                                        <tr key={`heading_${quality}`} className="print-quality-heading-row">
                                            <td colSpan={9}>{quality}</td>
                                        </tr>,
                                        ...rows,
                                    ];
                                }) : (batch.consolidatedItems || []).map(item => {
                                    const isUnavailable = item.status === 'unavailable';
                                    const totalQty = parseFloat(item.totalQuantity) || 0;
                                    const receivedTotal = parseFloat(item.receivedQuantity) || 0;
                                    const allocatedToOrders = item.sourceOrders?.reduce((sum, so) => sum + (so.allocatedQuantity || 0), 0) || 0;
                                    const toStockQty = Math.max(0, receivedTotal - allocatedToOrders);
                                    const pendingQty = isUnavailable ? 0 : Math.max(0, totalQty - receivedTotal);
                                    return (
                                        <tr key={item.sku} style={isUnavailable ? { opacity: 0.6 } : {}}>
                                            <td className="print-image-cell">
                                                {item.imageUrl ? (
                                                    <img src={item.imageUrl} alt={item.sku} className="print-item-image" loading="lazy" />
                                                ) : (
                                                    <div className="print-no-image">No Image</div>
                                                )}
                                            </td>
                                            <td className="sku-cell">
                                                <span style={isUnavailable ? { textDecoration: 'line-through' } : {}}>{item.sku}</span>
                                                {isUnavailable && <span style={{ color: '#b91c1c', fontSize: '0.75rem', marginLeft: '4px' }}>(Unavailable)</span>}
                                            </td>
                                            <td>{item.fabricName}</td>
                                            <td className="num-col">{item.totalQuantity.toFixed(2)}</td>
                                            <td className="num-col">{receivedTotal.toFixed(2)}</td>
                                            <td className="num-col">{allocatedToOrders.toFixed(2)}</td>
                                            <td className="num-col">{toStockQty.toFixed(2)}</td>
                                            <td className="num-col">{pendingQty.toFixed(2)}</td>
                                            <td>
                                                <ul className="source-orders-list">
                                                    {item.sourceOrders?.map(order => (
                                                        <li key={order.lineItemId}>
                                                            <span>#{formatBatchOrderNumber(order.orderNumber, order.source)}</span>
                                                            <span>{order.quantity.toFixed(2)}m</span>
                                                        </li>
                                                    ))}
                                                </ul>
                                            </td>
                                        </tr>
                                    );
                                }))}
                            </tbody>
                        </table>
                    </div>

                    <footer className="print-footer">
                        {/* Page number is now added via CSS */}
                    </footer>
                </div>
            </div>
        </>
    );
}

export default ProductionBatchDetailsPage;
